import React, { useEffect, useRef, useState } from 'react';
import { CloseIcon, FOCUS, MOTION } from '../styles';
import { LayoutRecord, Tab } from './layoutTypes';
import { IconButton, buttonClass } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { Field } from '../../components/ui/Field';
import Menu, { type MenuEntry, type MenuItem } from '../../components/ui/Menu';
import SegTrack from '../../components/ui/SegTrack';
import Tag from '../../components/ui/Tag';
import Popover from '../../components/ui/Popover';
import Tooltip from '../../components/ui/Tooltip';
import Reveal from '../../components/ui/Reveal';
import Appear from '../../components/ui/Appear';
import FadeIn from '../../components/ui/FadeIn';

export const MAX_PERSON_TABS = 12;

export interface ProfileBarProps {
  tabs: Tab[];
  activeTab: string;
  onSelectTab: (name: string) => void;
  onAddTab: () => void;
  onRenameTab: (from: string, to: string) => void;
  onRemoveTab: (name: string) => void;
  onColumnsChange: (name: string, columns: 1 | 2 | 3) => void;
  profiles: LayoutRecord[];
  activeProfile: LayoutRecord | null;
  dirty: boolean;
  defaultId: string | null;
  onPickProfile: (record: LayoutRecord | null) => void;
  onSave: () => void;
  onSaveAs: (name: string, shared: boolean) => void;
  onSetDefault: () => void;
  onDelete: () => void;
  onToggleFields: () => void;
  fieldsOpen: boolean;
  fieldsId?: string;
  /** A one-line failure, shown in the bar until the next action. */
  status?: string | null;
  busy?: boolean;
}

const KEY = `np-state inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-sm ${FOCUS} ${MOTION}`;

const ChevronDown: React.FC = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-3.5 w-3.5 shrink-0">
    <path fill="currentColor" d="M3.5 6 8 10.5 12.5 6 11.4 5 8 8.4 4.6 5z" />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
    <path fill="currentColor" d="M7.25 2.5h1.5v4.75h4.75v1.5H8.75v4.75h-1.5V8.75H2.5v-1.5h4.75z" />
  </svg>
);

const FieldsIcon: React.FC = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
    <path fill="currentColor" d="M2 3h2v2H2zM6 3h8v2H6zM4 7h2v2H4zM8 7h6v2H8zM4 11h2v2H4zM8 11h6v2H8z" />
  </svg>
);

const ownerName = (r: LayoutRecord) => [r.owner?.firstname, r.owner?.lastname].filter(Boolean).join(' ');

/* ── Tabs ── */

const TabKey: React.FC<{
  tab: Tab;
  active: boolean;
  onSelect: () => void;
  onRename?: (to: string) => void;
}> = ({ tab, active, onSelect, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tab.name);
  const label = tab.builtin === 'analyst' && tab.plots.length > 0
    ? <>{tab.name}<span className="text-xs tabular-nums opacity-80">{tab.plots.length}</span></>
    : tab.name;

  if (editing && onRename) {
    const commit = () => {
      setEditing(false);
      const next = draft.trim();
      if (next && next !== tab.name) onRename(next);
    };
    return (
      <div className="w-32">
        <Field
          size="sm"
          autoFocus
          value={draft}
          maxLength={40}
          aria-label={`Rename tab ${tab.name}`}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onFocus={e => e.currentTarget.select()}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setEditing(false); setDraft(tab.name); }
          }}
        />
      </div>
    );
  }

  const key = (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      data-tab={tab.name}
      onClick={onSelect}
      onDoubleClick={onRename ? () => { setDraft(tab.name); setEditing(true); } : undefined}
      onKeyDown={onRename ? (e) => {
        if (e.key === 'F2') { e.preventDefault(); setDraft(tab.name); setEditing(true); }
      } : undefined}
      className={`${KEY} ${active ? 'bg-primary/15 font-medium text-primary-ink' : 'text-on-surface-variant hover:text-on-surface'}`}
    >
      {label}
    </button>
  );
  return onRename ? <Tooltip label="Double-click or press F2 to rename">{key}</Tooltip> : key;
};

/* ── The bar ── */

const ProfileBar: React.FC<ProfileBarProps> = ({
  tabs, activeTab, onSelectTab, onAddTab, onRenameTab, onRemoveTab, onColumnsChange,
  profiles, activeProfile, dirty, defaultId, onPickProfile, onSave, onSaveAs, onSetDefault,
  onDelete, onToggleFields, fieldsOpen, fieldsId, status, busy,
}) => {
  const active = tabs.find(t => t.name === activeTab) || null;
  const personTabs = tabs.filter(t => !t.builtin);
  const activePerson = active && !active.builtin ? active : null;

  const [confirmTab, setConfirmTab] = useState<string | null>(null);
  useEffect(() => { setConfirmTab(null); }, [activeTab]);

  const tablistRef = useRef<HTMLDivElement>(null);
  const onTablistKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    if ((e.target as HTMLElement).tagName === 'INPUT') return;
    const i = tabs.findIndex(t => t.name === activeTab);
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
    if (!next) return;
    e.preventDefault();
    onSelectTab(next.name);
    requestAnimationFrame(() => {
      Array.from(tablistRef.current?.querySelectorAll<HTMLElement>('[data-tab]') || [])
        .find(el => el.dataset.tab === next.name)?.focus();
    });
  };

  /* Profile picker */
  /* Save as */
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);
  const saveAsTrigger = useRef<HTMLButtonElement>(null);
  const openSaveAs = () => {
    setSaveName(activeProfile ? `${activeProfile.name} copy`.slice(0, 80) : '');
    setSaveShared(false);
    setSaveAsOpen(o => !o);
  };

  const mine = profiles.filter(p => p.mine);
  const team = profiles.filter(p => !p.mine);
  const label = activeProfile
    ? (dirty ? `${activeProfile.name}, edited` : activeProfile.name)
    : (dirty ? 'Unsaved view' : 'Pick a profile');
  const isDefault = !!activeProfile && defaultId === activeProfile.id;

  const profileItem = (r: LayoutRecord): MenuItem => ({
    key: r.id,
    label: r.name,
    kind: 'radio',
    checked: activeProfile?.id === r.id,
    onSelect: () => onPickProfile(r),
    meta: (
      <span className="flex shrink-0 items-center gap-1.5">
        {!r.mine && ownerName(r) && <span className="max-w-[7rem] truncate">{ownerName(r)}</span>}
        {r.mine && r.shared && <span>Shared</span>}
        {defaultId === r.id && <Tag variant="code" size="sm">Default</Tag>}
      </span>
    ),
  });

  const profileNote = [
    dirty ? 'Switching drops your unsaved changes.' : '',
    profiles.length === 0 ? 'No saved profiles yet. Save this view to make one.' : '',
  ].filter(Boolean).join(' ') || undefined;

  const profileEntries: MenuEntry[] = [
    ...(mine.length > 0 ? [{ group: 'Mine', items: mine.map(profileItem) }] : []),
    ...(team.length > 0 ? [{ group: 'Team', items: team.map(profileItem) }] : []),
    {
      items: [
        { key: 'blank', label: 'Start a blank view', onSelect: () => onPickProfile(null) },
        ...(activeProfile
          ? [{ key: 'default', label: 'Set as my default', kind: 'checkbox' as const, checked: isDefault, disabled: busy, onSelect: onSetDefault }]
          : []),
        ...(activeProfile?.mine
          ? [{
            key: 'delete',
            label: `Delete ${activeProfile.name}`,
            tone: 'danger' as const,
            disabled: busy,
            confirm: { question: `Delete ${activeProfile.name}?`, yes: 'Delete', no: 'Keep' },
            onSelect: onDelete,
          }]
          : []),
      ],
    },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onToggleFields}
          aria-expanded={fieldsOpen}
          aria-controls={fieldsId}
          className={`${KEY} ${fieldsOpen ? 'bg-primary/15 font-medium text-primary-ink' : 'bg-surface-container-high text-on-surface-variant hover:text-on-surface'}`}
        >
          <FieldsIcon />
          Fields
        </button>

        <div
          ref={tablistRef}
          role="tablist"
          aria-label="Explorer tabs"
          onKeyDown={onTablistKey}
          className="flex min-w-0 max-w-full items-center gap-0.5 overflow-x-auto rounded-full bg-surface-container-high p-0.5"
        >
          {tabs.map(t => (
            <TabKey
              key={t.builtin ? `builtin-${t.builtin}` : `tab-${t.name}`}
              tab={t}
              active={t.name === activeTab}
              onSelect={() => onSelectTab(t.name)}
              onRename={t.builtin ? undefined : (to) => onRenameTab(t.name, to)}
            />
          ))}
          <Tooltip label="Add a tab of your own" note={personTabs.length >= MAX_PERSON_TABS ? 'A profile holds 12 tabs' : undefined}>
            <IconButton
              size="sm"
              label="Add a tab"
              onClick={onAddTab}
              disabled={personTabs.length >= MAX_PERSON_TABS}
            >
              <PlusIcon />
            </IconButton>
          </Tooltip>
        </div>

        <Appear as="div" show={!!activePerson}>
        {activePerson && (
          <div className="flex items-center gap-1.5">
            <SegTrack
              label="Columns"
              size="sm"
              wrap
              options={[{ key: '1', label: '1' }, { key: '2', label: '2' }, { key: '3', label: '3' }]}
              value={String(activePerson.columns)}
              onChange={k => onColumnsChange(activePerson.name, Number(k) as 1 | 2 | 3)}
            />
            <FadeIn key={confirmTab === activePerson.name ? 'confirm' : 'remove'} className="flex items-center">
            {confirmTab === activePerson.name ? (
              <span className="flex items-center gap-1.5 text-sm text-on-surface" role="group" aria-label="Confirm removing the tab">
                <span className="whitespace-nowrap">
                  Remove {activePerson.name}{activePerson.plots.length ? ` and its ${activePerson.plots.length} ${activePerson.plots.length === 1 ? 'plot' : 'plots'}` : ''}?
                </span>
                <button type="button" autoFocus onClick={() => { setConfirmTab(null); onRemoveTab(activePerson.name); }} className={buttonClass('danger', 'sm')}>Remove</button>
                <button type="button" onClick={() => setConfirmTab(null)} className={buttonClass('quiet', 'sm')}>Keep</button>
              </span>
            ) : (
              <Tooltip label="Remove this tab">
                <IconButton
                  size="sm"
                  label={`Remove tab ${activePerson.name}`}
                  onClick={() => (activePerson.plots.length ? setConfirmTab(activePerson.name) : onRemoveTab(activePerson.name))}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            )}
            </FadeIn>
          </div>
        )}
        </Appear>

        <div className="ml-auto flex min-w-0 items-center gap-1.5">
          <div className="min-w-0">
            <Menu
              label="Profiles"
              note={profileNote}
              items={profileEntries}
              trigger={(props) => (
                <button
                  {...props}
                  onClick={() => {
                    setSaveAsOpen(false);
                    props.onClick?.();
                  }}
                  className={`${KEY} max-w-[16rem] bg-surface-container-high ${activeProfile || dirty ? 'text-on-surface' : 'text-on-surface-variant'}`}
                >
                  <span className="min-w-0 truncate">{label}</span>
                  <ChevronDown />
                </button>
              )}
            />
          </div>

          <Appear as="div" show={!!(activeProfile?.mine && dirty)}>
            <button type="button" onClick={onSave} disabled={busy} className={buttonClass('filled', 'sm')}>
              Save
            </button>
          </Appear>

          <div className="relative">
            <button
              ref={saveAsTrigger}
              type="button"
              aria-expanded={saveAsOpen}
              onClick={openSaveAs}
              disabled={busy}
              className={buttonClass('tonal', 'sm')}
            >
              Save as
            </button>
            <Popover open={saveAsOpen} anchor={saveAsTrigger} onClose={() => setSaveAsOpen(false)} label="Save as a new profile" align="end" width="md" hideClose>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  const name = saveName.trim();
                  if (!name) return;
                  setSaveAsOpen(false);
                  onSaveAs(name, saveShared);
                }}
                className="flex flex-col gap-2"
              >
                <label className="flex flex-col gap-1 text-xs text-on-surface-variant">
                  Profile name
                  <Field
                    size="sm"
                    autoFocus
                    value={saveName}
                    maxLength={80}
                    onChange={e => setSaveName(e.target.value)}
                    placeholder="Vibration"
                  />
                </label>
                <Checkbox
                  checked={saveShared}
                  onChange={e => setSaveShared(e.target.checked)}
                  label="Share with the team"
                />
                <div className="flex justify-end gap-1.5">
                  <button type="button" onClick={() => setSaveAsOpen(false)} className={buttonClass('quiet')}>Cancel</button>
                  <button type="submit" disabled={!saveName.trim()} className={buttonClass('filled')}>Save</button>
                </div>
              </form>
            </Popover>
          </div>
        </div>
      </div>
      <Reveal show={!!status}>
        <p role="status" className="truncate px-1 text-xs text-on-surface" title={status}>{status}</p>
      </Reveal>
    </div>
  );
};

export default React.memo(ProfileBar);
